import { useEffect, useState } from "react";
import { IStringLocalizer } from "../../../utilities/IStringLocalizer";
import { TableColumn, TableRow, ToxnotReactTable } from "../ToxnotReactTable";
import { ToxnotServiceClient } from "../../../store/ToxnotServiceClient";
import { LoadingWrapper } from "../LoadingWrapper";
import { ScipCharacteristic, ScipCharacteristicType } from "../../../store/Models/Surveys/SurveyDataModel";
import { DropdownMenu } from "../DropdownMenu";
import TextInput from "../TextInput";
import { LoadingSpinnerSize } from "../LoadingSpinner";

interface CharacteristicsProps {
    localizer: IStringLocalizer;
    onValueChanged: (val: any) => void;
    value: string
}

/**
 * Renders the Characteristics for SCIP. This follows the overall pattern and look.
 * from the .NET side.
 */
export const Characteristics = (props: CharacteristicsProps) => {
    const serviceClient = new ToxnotServiceClient();
    const [loading, setLoading] = useState(true);
    const [characteristics, setCharacteristics] = useState<ScipCharacteristic[]>([]); 
    const [colourTable, setColourTable] = useState<{ [code: string]: string }>({});
    const [weightUnitTable, setWeightUnitTable] = useState<{ [code: string]: string }>({});
    const [volumeUnitTable, setVolumeUnitTable] = useState<{ [code: string]: string }>({});
    const [densityUnitTable, setDensityUnitTable] = useState<{ [code: string]: string }>({});
    const [heightLengthWidthDiameterUnitTable, setHeightLengthWidthDiameterUnitTable] = useState<{ [code: string]: string }>({});
    const newCharacteristic = (): ScipCharacteristic => ({ value: "", type: ScipCharacteristicType.Colour, typeOtherText: "", unitId: "" });

    useEffect(() => {
        let doNotRender = false;

        (async () => {
            const response = await serviceClient.scipIuclidStaticDataGet();

            if (!response.success || response.data === null) {
                alert(props.localizer.localizeString("Sorry, unable to fetch data from service."));
                return;
            }

            if(response.data.colourTable){
                for(var color in response.data.colourTable){
                    response.data.colourTable[color] = props.localizer.localizeString(response.data.colourTable[color]);
                }
            }

            if (!doNotRender) {
                setColourTable(response.data.colourTable);
                setWeightUnitTable(response.data.weightUnitTable);
                setVolumeUnitTable(response.data.volumeUnitTable);
                setDensityUnitTable(response.data.densityUnitTable);
                setHeightLengthWidthDiameterUnitTable(response.data.heightLengthWidthDiameterUnitTable);

                setLoading(false);
            }
        })();

        let initalCharacteristics: ScipCharacteristic[];

        try {
            initalCharacteristics = JSON.parse(props.value) as ScipCharacteristic[];
        }
        catch {
            initalCharacteristics = [];
        }

        if (!doNotRender) {
            updateCharacteristics(initalCharacteristics);
        }

        return () => {
            doNotRender = true;
        };
    }, []);

    function addCharacteristic_onClick() {
        const newCharacteristics = [...characteristics];
        newCharacteristics.push(newCharacteristic());
        setCharacteristics(newCharacteristics);
    }

    function getHeaderRow(): TableColumn<ScipCharacteristic>[] {
        return [
            {
                value: props.localizer.localizeString("Type")
            },
            {
                value: props.localizer.localizeString("Units")
            },
            {
                value: props.localizer.localizeString("Value")
            },
        ];
    }

    function updateCharacteristics(newCharacteristics: ScipCharacteristic[]) {
        setCharacteristics(newCharacteristics);
        props.onValueChanged(newCharacteristics);
    }

    const onTypeDropdownChanged = (o: ScipCharacteristicType, rowIndex: number) => {
        if (!shouldAddNewType(o, rowIndex)) {
            return;
        }

        const newCharacteristics = [...characteristics];

        const sameAsBefore = o === newCharacteristics[rowIndex].type;

        if (sameAsBefore) {
            return;
        }

        newCharacteristics[rowIndex].type = o;
        newCharacteristics[rowIndex].value = "";
        newCharacteristics[rowIndex].typeOtherText = "";
        newCharacteristics[rowIndex].unitId = Object.keys(typeToUnitTableMap[o])[0] ?? "";

        updateCharacteristics(newCharacteristics);
    }

    const onUnitDropdownChanged = (u: string, rowIndex: number) => {
        const newCharacteristics = [...characteristics];
        newCharacteristics[rowIndex].unitId = u;

        updateCharacteristics(newCharacteristics);
    }

    const onColorDropDownChanged = (c: string, rowIndex: number) => {
        const newCharacteristics = [...characteristics];
        newCharacteristics[rowIndex].value = c;
        newCharacteristics[rowIndex].unitId = "";

        updateCharacteristics(newCharacteristics);
    }

    const onValueChanged = (v: string, rowIndex: number) => {
        const newCharacteristics = [...characteristics];
        newCharacteristics[rowIndex].value = v;

        updateCharacteristics(newCharacteristics);
    }

    const onOtherTextChanged = (v: string, rowIndex: number) => {
        const newCharacteristics = [...characteristics];
        newCharacteristics[rowIndex].typeOtherText = v;

        updateCharacteristics(newCharacteristics);
    }

    const typeToUnitTableMap: { [type: string]: { [code: string]: string }} = {
        [ScipCharacteristicType.Height]: heightLengthWidthDiameterUnitTable,
        [ScipCharacteristicType.Length]: heightLengthWidthDiameterUnitTable,
        [ScipCharacteristicType.Width]: heightLengthWidthDiameterUnitTable,
        [ScipCharacteristicType.Diameter]: heightLengthWidthDiameterUnitTable,
        [ScipCharacteristicType.Density]: densityUnitTable,
        [ScipCharacteristicType.Weight]: weightUnitTable,
        [ScipCharacteristicType.Volume]: volumeUnitTable,
        [ScipCharacteristicType.Colour]: colourTable,
        [ScipCharacteristicType.Other]: {}
    };

    function shouldAddNewType(newType: ScipCharacteristicType, rowIndex: number) {
        // This is to prevent bug on initial render. Characterisitcs already have their
        // types from database but the dropdown runs an option changed operation becuase
        // of the inital value. This causes an apparent duplicate. Preventing that here.
        if (characteristics[rowIndex].type === newType) {
            return true;
        }

        const allowedDuplicateTypes = [ScipCharacteristicType.Colour, ScipCharacteristicType.Other];

        if (allowedDuplicateTypes.includes(newType)) {
            return true;
        }

        const selectedTypes = characteristics.map(c => c.type);

        if (!selectedTypes.includes(newType)) {
            return true;
        }

        alert(props.localizer.localizeString("You can only have one characteristic of that type"));

        return false;
    }

    function removeCategory_click(index: number) {
        const newCharacteristics = [...characteristics.filter((c, i) => i !== index)];

        updateCharacteristics(newCharacteristics);
    }

    const getRow = (characteristic: ScipCharacteristic, index: number): TableRow => {
        return {
            cols: [
                <div>
                    <DropdownMenu
                        options={Object.values(ScipCharacteristicType)}
                        onOptionSelected={(o) => onTypeDropdownChanged(o, index)}
                        getFriendlyNameForOptions={(o) => props.localizer.localizeString(o.toString())}
                        getUniqueValueForOption={(o) => o}
                        preselectedOption={characteristic.type}
                        value={characteristic.type}
                    />
                    {characteristic.type === ScipCharacteristicType.Other &&
                        <TextInput onValueChange={(v) => onOtherTextChanged(v, index)} value={characteristic.typeOtherText} placeholder={props.localizer.localizeString("Type")} />}
                </div>
                ,
                <>
                    {(characteristic.type === ScipCharacteristicType.Colour ||
                        characteristic.type === ScipCharacteristicType.Other) ?
                    null :
                    <DropdownMenu
                        options={Object.keys(typeToUnitTableMap[characteristic.type])}
                        onOptionSelected={(o) => onUnitDropdownChanged(o, index)}
                        getFriendlyNameForOptions={(o) => props.localizer.localizeString(typeToUnitTableMap[characteristic.type][o])}
                        getUniqueValueForOption={(o) => o}
                        preselectedOption={Object.keys(typeToUnitTableMap[characteristic.type])[0]}
                        value={characteristic.unitId}
                    />}
                </>,
                <div className="form-inline">
                    {characteristic.type !== ScipCharacteristicType.Colour ?
                        <TextInput onValueChange={(v) => onValueChanged(v, index)} value={characteristic.value} /> :
                        <DropdownMenu
                            options={Object.keys(typeToUnitTableMap[characteristic.type])}
                            onOptionSelected={(o) => onColorDropDownChanged(o, index)}
                            getFriendlyNameForOptions={(o) => props.localizer.localizeString(typeToUnitTableMap[characteristic.type][o])}
                            getUniqueValueForOption={(o) => o}
                            value={characteristic.value}
                        />
                    }
                    <button className="btn btn-sm btn-toxnot-danger m-l-5" onClick={e => removeCategory_click(index)}><span className="glyphicon glyphicon-remove"></span></button>
                </div>
            ]
        }
    }

    return (
        <div>
            <LoadingWrapper loading={loading} spinnerSize={LoadingSpinnerSize.Small}>
                <button className="btn btn-default m-b-5" onClick={addCharacteristic_onClick}>{props.localizer.localizeString("Add Characteristic")}</button>
                <div className="characteristics-table-container">
                    {characteristics.length > 0 && <ToxnotReactTable
                        localizer={props.localizer}
                        allRowModels={characteristics}
                        getRowForTable={getRow}
                        headerRow={getHeaderRow()}
                        enablePaging={false}
                    />}
                </div>
            </LoadingWrapper>
        </div>
    );
}